import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axiosConn from '../../axiosConn';
import { getHitLabApiMessage } from './hitLabUtils';
import {
  FEEDBACK_REWARD_PASS_REWARD_AMOUNT_JGK,
  FEEDBACK_REWARD_PASS_REWARD_CASHOUT_VALUE_USD,
  FEEDBACK_REWARD_PASS_REWARD_COPY,
} from './hitLabRadioFeedbackRewardPassApi';

const initialData = {
  summary: {},
  rows: [],
};

const formatCount = (value) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

const formatUsd = (value) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const getRewardAmountJgk = (row) => {
  const amount = Number(row?.rewardAmountJgk);
  return Number.isFinite(amount) && amount > 0
    ? Math.round(amount)
    : FEEDBACK_REWARD_PASS_REWARD_AMOUNT_JGK;
};

const getRewardCashoutValueUsd = (row) => {
  const value = Number(row?.rewardCashoutValueUsd);
  return Number.isFinite(value) && value > 0
    ? value
    : FEEDBACK_REWARD_PASS_REWARD_CASHOUT_VALUE_USD;
};

const getRewardCopy = (row) =>
  `${formatCount(getRewardAmountJgk(row))} JGK - a $${formatUsd(
    getRewardCashoutValueUsd(row),
  )} gross cashout value through JG Player`;

const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Not set';
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getProof = (row) => row?.listenProofSnapshot || row?.progress || {};

const getProofTracks = (row) =>
  Array.isArray(getProof(row)?.tracks) ? getProof(row).tracks : [];

const getProofTrackDisplay = (proof = {}, tracks = []) => {
  const visibleTracks = tracks.slice(0, 5);
  const proofCount = Number(proof.distinctTrackCount);
  const totalTrackCount =
    Number.isFinite(proofCount) && proofCount > 0 ? proofCount : tracks.length;
  return {
    visibleTracks,
    hiddenTrackCount: Math.max(0, totalTrackCount - visibleTracks.length),
  };
};

const getTrackLabel = (track = {}) => {
  const title = String(track.title || track.trackId || 'Record').trim();
  const artist = String(track.artistName || '').trim();
  return artist ? `${title} - ${artist}` : title;
};

const getStatusLabel = (status) =>
  String(status || 'unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getNotificationLabel = (row) => {
  const summary = row?.notificationDeliverySummary || {};
  if (summary.failed) return 'Failed';
  if (row?.approvalNotificationId) return 'Recorded';
  if (row?.status === 'approved') return 'Not recorded';
  return 'Not needed';
};

export default function HitLabRadioListenerFeedbackRewardsAdmin({
  authConfig,
  refreshKey = 0,
}) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [rejectReasons, setRejectReasons] = useState({});

  const rows = Array.isArray(data.rows) ? data.rows : [];
  const summary = data.summary || {};
  const reviewRows = useMemo(
    () => rows.filter((row) => ['submitted', 'approved', 'rejected'].includes(row.status)),
    [rows],
  );

  const loadRewards = useCallback(() => {
    if (!authConfig) return Promise.resolve();
    setLoading(true);
    setError('');
    return axiosConn
      .get('hitlab/admin/radio/listener-feedback-rewards', authConfig)
      .then((response) => {
        setData(response.data?.data || initialData);
      })
      .catch((err) => {
        setError(
          getHitLabApiMessage(
            err,
            'Listener Feedback Rewards could not be loaded.',
          ),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [authConfig]);

  useEffect(() => {
    void loadRewards();
  }, [loadRewards, refreshKey]);

  const approveReward = async (row) => {
    if (!authConfig || !row?.id || busyId) return;
    const rewardCopy = getRewardCopy(row);
    const confirmed = window.confirm(
      `You are approving this feedback pass and issuing ${rewardCopy} through the existing JG Player wallet system. Approval will also trigger the standard notification fanout: email, in-app, and web push. This action must be idempotent and audited.`,
    );
    if (!confirmed) return;
    setBusyId(row.id);
    setError('');
    setMessage('');
    try {
      await axiosConn.post(
        `hitlab/admin/radio/listener-feedback-rewards/${row.id}/approve`,
        { reviewNote: 'Approved from HitLab admin.' },
        authConfig,
      );
      setMessage('Feedback reward approved.');
      await loadRewards();
    } catch (err) {
      setError(
        getHitLabApiMessage(err, 'Feedback reward could not be approved.'),
      );
    } finally {
      setBusyId('');
    }
  };

  const rejectReward = async (row) => {
    if (!authConfig || !row?.id || busyId) return;
    const reason = String(rejectReasons[row.id] || '').trim();
    if (!reason) {
      setError('Enter a rejection reason before rejecting this pass.');
      return;
    }
    setBusyId(row.id);
    setError('');
    setMessage('');
    try {
      await axiosConn.post(
        `hitlab/admin/radio/listener-feedback-rewards/${row.id}/reject`,
        { reason },
        authConfig,
      );
      setMessage('Feedback pass rejected.');
      setRejectReasons((current) => ({ ...current, [row.id]: '' }));
      await loadRewards();
    } catch (err) {
      setError(
        getHitLabApiMessage(err, 'Feedback reward could not be rejected.'),
      );
    } finally {
      setBusyId('');
    }
  };

  return (
    <section
      className="hitlab-radio-feedback-rewards-admin"
      aria-label="Listener Feedback Rewards"
    >
      <div className="hitlab-radio-feedback-rewards-admin-header">
        <div>
          <p className="hitlab-kicker">LISTENER FEEDBACK REWARDS</p>
          <h2>Listener Feedback Rewards</h2>
          <p>
            Review submitted JG Radio feedback passes before issuing{' '}
            {FEEDBACK_REWARD_PASS_REWARD_COPY}.
          </p>
        </div>
        <button
          type="button"
          className="hitlab-secondary-btn"
          disabled={loading}
          onClick={loadRewards}
        >
          {loading ? 'Loading...' : 'Refresh rewards'}
        </button>
      </div>

      {error ? <div className="hitlab-alert hitlab-alert-error">{error}</div> : null}
      {message ? <div className="hitlab-alert">{message}</div> : null}

      <div className="hitlab-radio-feedback-rewards-summary">
        <article>
          <span>Pending review</span>
          <strong>{formatCount(summary.pendingReview)}</strong>
        </article>
        <article>
          <span>Approved</span>
          <strong>{formatCount(summary.approved)}</strong>
        </article>
        <article>
          <span>Rejected</span>
          <strong>{formatCount(summary.rejected)}</strong>
        </article>
        <article>
          <span>Reward issued</span>
          <strong>{formatCount(summary.rewardIssued)}</strong>
        </article>
        <article>
          <span>Notification sent</span>
          <strong>{formatCount(summary.notificationRecorded)}</strong>
        </article>
      </div>

      {reviewRows.length ? (
        <div className="hitlab-radio-feedback-rewards-list">
          {reviewRows.map((row) => {
            const proof = getProof(row);
            const tracks = getProofTracks(row);
            const { visibleTracks, hiddenTrackCount } = getProofTrackDisplay(
              proof,
              tracks,
            );
            const isSubmitted = row.status === 'submitted';
            return (
              <article key={row.id} className="hitlab-radio-feedback-reward-row">
                <div className="hitlab-radio-feedback-reward-row-main">
                  <div>
                    <strong>{row.firstName || row.contactEmail || row.player}</strong>
                    <span>
                      {row.contactEmail || row.player || 'Unknown listener'} -{' '}
                      {getStatusLabel(row.status)}
                    </span>
                    <small>
                      Submitted {formatDate(row.passSubmittedAt)} - Source{' '}
                      {row.source || 'direct'}
                    </small>
                  </div>
                  <div>
                    <span>Records heard</span>
                    <strong>{formatCount(proof.distinctTrackCount)} / {formatCount(proof.threshold || 5)}</strong>
                    <small>{getNotificationLabel(row)}</small>
                  </div>
                </div>

                <div className="hitlab-radio-feedback-reward-proof">
                  {visibleTracks.map((track) => (
                    <span key={track.trackId}>{getTrackLabel(track)}</span>
                  ))}
                  {hiddenTrackCount > 0 ? (
                    <span className="hitlab-radio-feedback-reward-proof-more">
                      +{formatCount(hiddenTrackCount)} more qualified{' '}
                      {hiddenTrackCount === 1 ? 'record' : 'records'} heard
                    </span>
                  ) : null}
                </div>

                <div className="hitlab-radio-feedback-reward-feedback">
                  <p>
                    <strong>Role:</strong> {row.listenerRole || 'Not provided'} -{' '}
                    {row.musicTaste || 'No music taste'}
                  </p>
                  <p>
                    <strong>Standout:</strong>{' '}
                    {row.standoutReason || 'No standout reason'}
                  </p>
                  <p>
                    <strong>Connected:</strong>{' '}
                    {row.whatConnected || 'No connected detail'}
                  </p>
                  <p>
                    <strong>Did not connect:</strong>{' '}
                    {row.whatDidNotConnect || 'No limiter detail'}
                  </p>
                  <p>
                    <strong>Room:</strong>{' '}
                    {row.roomExperience || 'No room detail'}
                  </p>
                </div>

                {isSubmitted ? (
                  <div className="hitlab-radio-feedback-reward-actions">
                    <label>
                      Reject reason
                      <input
                        value={rejectReasons[row.id] || ''}
                        onChange={(event) => {
                          const value = event.target.value;
                          setRejectReasons((current) => ({
                            ...current,
                            [row.id]: value,
                          }));
                        }}
                        placeholder="Required before rejecting"
                      />
                    </label>
                    <button
                      type="button"
                      className="hitlab-primary-btn"
                      disabled={busyId === row.id}
                      onClick={() => approveReward(row)}
                    >
                      {busyId === row.id
                        ? 'Working...'
                        : `Issue ${formatCount(getRewardAmountJgk(row))} JGK`}
                    </button>
                    <button
                      type="button"
                      className="hitlab-secondary-btn"
                      disabled={busyId === row.id}
                      onClick={() => rejectReward(row)}
                    >
                      Reject pass
                    </button>
                  </div>
                ) : (
                  <div className="hitlab-radio-feedback-reward-status">
                    <span>{getStatusLabel(row.status)}</span>
                    <span>
                      Reward {row.rewardTxnId ? 'issued' : 'not issued'} -{' '}
                      Notification {getNotificationLabel(row).toLowerCase()}
                    </span>
                    {row.rejectedReason ? <p>{row.rejectedReason}</p> : null}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="hitlab-alert">
          No submitted Listener Feedback Rewards are waiting in the review queue.
        </div>
      )}
    </section>
  );
}
